/**
 * `useAssistantHostBridge` — `RecordDetailInline`'s side of the bridge (#765,
 * spec §8.2 п.1–3).
 *
 * A citation click can land while the Score or Notes tab is open. The
 * `AudioPlayer` is mounted only under the transcript tab, and its queued
 * segment lives inside the mounted player (reset on re-init), so the request
 * must be sent AFTER the tab switched and the audio decoded — not before.
 * This hook keeps the request until both hold, then plays it once. Chats
 * have no player: the request becomes a seek after the tab switch, and the
 * transcript's own scroll effect follows `currentTime` on mount.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { AssistantHost, AssistantHostEntity } from '@/contexts/AssistantHostContext';

export type RecordDetailTab = 'transcript' | 'notes' | 'score';

/**
 * How the assistant treats a record: a call plays quotes; anything else —
 * a chat, an email — scrolls the transcript to them. `undefined` while the
 * record is unknown: no host yet (Codex P2, round 10; owner's review 17.09:
 * an email record used to get no host at all, its sidebar entry greyed out).
 */
export function assistantHostKind(
  type: 'call' | 'chat' | 'email' | undefined
): 'call' | 'chat' | undefined {
  if (type === undefined) return undefined;
  return type === 'call' ? 'call' : 'chat';
}

export interface AssistantHostBridgeParams {
  recordId: string;
  /** What the panel calls this record (#922) — the header's own wording. */
  label: string;
  recordType: 'call' | 'chat' | undefined;
  /** The record's CRM entity (#765 Ф2); `null` when it has none. */
  entity: AssistantHostEntity | null;
  activeTab: RecordDetailTab;
  setActiveTab: (tab: RecordDetailTab) => void;
  /** Decoded and seekable audio for the CURRENT audio url. */
  /** The record has a recording at all; a call without one reads like a chat. */
  hasAudio: boolean;
  isAudioReady: boolean;
  playSegment: (startSeconds: number, durationSeconds: number) => void;
  seek: (startSeconds: number) => void;
  /** Where another record opens in place, when the host has such a place (the CRM tab). */
  openRecord?: (recordId: string, atSec?: number) => void;
}

interface PendingPlay {
  startSec: number;
  clipSeconds: number;
}

export function useAssistantHostBridge(
  params: AssistantHostBridgeParams,
  register: ((host: AssistantHost | null) => void) | null
): void {
  const {
    recordId,
    label,
    recordType,
    entity,
    activeTab,
    setActiveTab,
    hasAudio,
    isAudioReady,
    playSegment,
    seek,
    openRecord,
  } = params;
  // The entity arrives as a fresh object on every render of the record page.
  // Made stable ONCE here, over the four fields that can change, so the host
  // carries an object whose identity changes exactly when the entity does —
  // and every reader downstream (the drawer's topic) can rely on that
  // identity instead of splitting it into fields again (#1041 review).
  const entityType = entity?.entityType ?? null;
  const entityId = entity?.entityId ?? null;
  const entityInstallationId = entity?.installationId ?? null;
  const entityLabel = entity?.label ?? '';
  const stableEntity = useMemo<AssistantHostEntity | null>(
    () =>
      entityType !== null && entityId !== null
        ? {
            entityType,
            entityId,
            installationId: entityInstallationId,
            label: entityLabel,
          }
        : null,
    [entityType, entityId, entityInstallationId, entityLabel]
  );
  const pendingRef = useRef<PendingPlay | null>(null);
  // A request made while the transcript tab is already open changes none of
  // the effect's inputs; the tick re-runs the resolution below.
  const [tick, bump] = useReducer((n: number) => n + 1, 0);

  // A request outlives nothing but its record: the next or previous record
  // reuses this hook, and the queued timestamp would otherwise play against
  // the new recording once its audio is ready (Codex P2, round 18). Before
  // the resolution below, so a same-commit record change clears first.
  useEffect(() => {
    pendingRef.current = null;
  }, [recordId]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || activeTab !== 'transcript') return;
    if (recordType === 'chat' || !hasAudio) {
      pendingRef.current = null;
      seek(pending.startSec);
      return;
    }
    if (!isAudioReady) return;
    pendingRef.current = null;
    playSegment(pending.startSec, pending.clipSeconds);
  }, [tick, activeTab, hasAudio, isAudioReady, recordType, playSegment, seek]);

  const requestPlay = useCallback(
    (startSec: number, clipSeconds: number) => {
      pendingRef.current = { startSec, clipSeconds };
      if (activeTab !== 'transcript') setActiveTab('transcript');
      bump();
    },
    [activeTab, setActiveTab]
  );

  useEffect(() => {
    // Not a host until the record is known: while it loads, and on its
    // error or not-found state, `recordType` is undefined — a host then
    // would light the assistant button for a record that never loaded and
    // open a panel that can only fail (Codex P2, round 10).
    if (!register || recordType === undefined) return;
    register({
      recordId,
      label,
      recordType,
      entity: stableEntity,
      requestPlay,
      openRecord,
    });
    return () => register(null);
  }, [
    register,
    recordId,
    label,
    recordType,
    stableEntity,
    isAudioReady,
    requestPlay,
    openRecord,
  ]);
}
